import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getCheckoutStatus } from '../lib/api';
import { useCart } from '../context/CartContext';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const { clearCart } = useCart();

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const data = await getCheckoutStatus(sessionId);
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          clearCart(); // Clear cart on successful payment
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          // Continue polling
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts, clearCart]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  if (status === 'loading' || (status !== 'success' && status !== 'error' && status !== 'expired' && status !== 'pending')) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="checkout-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-ghrb-orange animate-spin mx-auto mb-4" />
          <p className="font-jakarta text-ghrb-gray-dark">Vérification du paiement...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="checkout-error">
        <div className="text-center px-4">
          <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-ghrb-black mb-4">
            Une erreur est survenue
          </h1>
          <p className="font-jakarta text-ghrb-gray-dark mb-8">
            Nous n'avons pas pu vérifier votre paiement. Veuillez contacter le support.
          </p>
          <Link to="/panier">
            <Button className="bg-ghrb-orange hover:bg-ghrb-orange-hover text-white rounded-full">
              Retour au panier
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="checkout-expired">
        <div className="text-center px-4">
          <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-ghrb-black mb-4">
            Session expirée
          </h1>
          <p className="font-jakarta text-ghrb-gray-dark mb-8">
            Votre session de paiement a expiré. Veuillez réessayer.
          </p>
          <Link to="/panier">
            <Button className="bg-ghrb-orange hover:bg-ghrb-orange-hover text-white rounded-full">
              Retour au panier
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-success-page" className="min-h-screen bg-white">
      <section className="py-20 lg:py-32">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>

            <h1 className="font-outfit text-3xl lg:text-4xl font-bold text-ghrb-black mb-4">
              Merci pour votre commande !
            </h1>

            <p className="font-jakarta text-lg text-ghrb-gray-dark mb-8">
              Votre paiement a été confirmé. Vous recevrez un email de confirmation sous peu.
            </p>

            {paymentData && (
              <div className="bg-ghrb-gray p-6 rounded-2xl mb-8 text-left">
                <h3 className="font-outfit font-semibold text-ghrb-black mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-ghrb-orange" />
                  Détails de la commande
                </h3>
                <div className="space-y-2 font-jakarta text-sm">
                  <div className="flex justify-between">
                    <span className="text-ghrb-gray-dark">Montant total</span>
                    <span className="text-ghrb-black font-medium">
                      {(paymentData.amount_total / 100).toFixed(2)}€
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ghrb-gray-dark">Statut</span>
                    <span className="text-green-600 font-medium">Payé</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/produits">
                <Button 
                  size="lg"
                  className="bg-ghrb-orange hover:bg-ghrb-orange-hover text-white font-jakarta font-semibold px-8 py-6 rounded-full"
                  data-testid="continue-shopping-success"
                >
                  Continuer mes achats
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/">
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-ghrb-black text-ghrb-black hover:bg-ghrb-black hover:text-white font-jakarta font-semibold px-8 py-6 rounded-full"
                  data-testid="back-home-btn"
                >
                  Retour à l'accueil
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CheckoutSuccessPage;
